/**
 * FO-491 (batch-66) — de eigen-uren-periode knippen op de kalenderperiode.
 *
 * De portal toonde bij "deze maand" hele ISO-weken ("september" liep van 31-8
 * t/m 4-10). De maandweergave telt voortaan alleen de dagen binnen de periode.
 * Twee dingen maken dat minder triviaal dan een datumfilter:
 *
 *  1. De splitsing basis / ATV-opbouw / overuren is per ISO-WEEK gedefinieerd
 *     (CAO: overuren is week-net, niet per dag opgeteld). Die blijft per week
 *     berekend; daarna rekenen we haar per DAG toe, zodat een week die over de
 *     maandgrens loopt zich over twee maanden laat verdelen.
 *  2. Elke dag moet in precies één maand tellen: de som van alle maanden is het
 *     jaartotaal. Daarom is de toerekening exact sluitend (in honderdsten van
 *     een uur) en nooit een afgeronde benadering per maand.
 *
 * Toerekenregel (één regel, voor elke week): chronologisch. De eerste gewerkte
 * uren van de week vullen de basis, daarna de ATV-band, het laatste stuk is
 * overuren. Dat is ook hoe week-net overwerk ontstaat: je maakt pas overuren
 * nadat de weeknorm vol is.
 *
 * Pure functies, zero deps (CLAUDE.md: geen datum-library). Datums zijn
 * kalenderdagen `yyyy-MM-dd`; die vergelijken als string chronologisch, dus er
 * komt geen tijdzone of DST aan te pas.
 */

import type { UrenSplitsing } from './week-overwerk'
import { getIsoWeek } from './uren-calculations'

const MS_PER_DAG = 86_400_000

/** Donderdag (index 3, ma = 0): het ISO-anker van een week. */
const ISO_ANKER_INDEX = 3

const MAAND_NAMEN = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
]

function utcDag(iso: string): Date {
  return new Date(`${iso}T00:00:00.000Z`)
}

function isoDag(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** Kalenderdag `iso` plus `dagen` (negatief = terug), als `yyyy-MM-dd`. */
export function plusDagen(iso: string, dagen: number): string {
  return isoDag(new Date(utcDag(iso).getTime() + dagen * MS_PER_DAG))
}

/** Aantal kalenderdagen van `van` t/m `totEnMet` (inclusief); 0 als `van` later ligt. */
function aantalDagenInclusief(van: string, totEnMet: string): number {
  if (van > totEnMet) return 0
  return Math.round((utcDag(totEnMet).getTime() - utcDag(van).getTime()) / MS_PER_DAG) + 1
}

/** Niet-eindig of negatief wordt 0 (zelfde verdediging als week-overwerk.ts). */
function klem(x: number): number {
  return Number.isFinite(x) && x > 0 ? x : 0
}

/** Uren (of km) naar hele honderdsten, zodat sommen exact sluiten. */
function naarCenti(x: number): number {
  return Math.round(klem(x) * 100)
}

// ─── Knippen ─────────────────────────────────────────────────────────────────

/**
 * Houd alleen de dagen binnen `[van, totEnMet]` over (beide grenzen inclusief).
 *
 * Bewust generiek: dezelfde knip geldt voor de dagenlijst die de monteur ziet
 * en voor de dag-aandelen waaruit de totalen worden opgeteld. Zo kan een dag
 * niet in de lijst staan en buiten het totaal vallen (of andersom).
 */
export function knipOpPeriode<T extends { date: string }>(
  dagen: readonly T[],
  van: string,
  totEnMet: string,
): T[] {
  return dagen.filter((d) => d.date >= van && d.date <= totEnMet)
}

// ─── Hele weken ──────────────────────────────────────────────────────────────

export interface HeleWekenBereik {
  /** Maandag van de ISO-week waarin `van` valt. */
  van: string
  /** Zondag van de ISO-week waarin `totEnMet` valt. */
  totEnMet: string
  eersteWeek: { year: number; week: number }
  laatsteWeek: { year: number; week: number }
}

/**
 * Rek een periode op tot hele ISO-weken (maandag t/m zondag): de variant van
 * de backoffice-weekstaatlijst. September 2026 wordt zo 31-08 t/m 04-10 (week
 * 36 t/m 40). De verloning doet dit NIET: die telt per kalenderdag van de maand.
 */
export function heleWekenBereik(van: string, totEnMet: string): HeleWekenBereik {
  const vanDag = utcDag(van)
  const totDag = utcDag(totEnMet)
  // getUTCDay: 0 = zondag. Terug naar maandag resp. vooruit naar zondag.
  const maandag = plusDagen(van, -((vanDag.getUTCDay() + 6) % 7))
  const zondag = plusDagen(totEnMet, (7 - totDag.getUTCDay()) % 7)
  return {
    van: maandag,
    totEnMet: zondag,
    eersteWeek: getIsoWeek(utcDag(maandag)),
    laatsteWeek: getIsoWeek(utcDag(zondag)),
  }
}

// ─── Een week tegen de periode ───────────────────────────────────────────────

export interface WeekBinnenPeriode {
  /** Eerste dag van de week die binnen de periode valt. */
  periodeStart: string
  /** Laatste dag van de week die binnen de periode valt. */
  periodeEind: string
  /** Kalenderdagen van de week vóór `van`. */
  dagenVoorPeriode: number
  /** Kalenderdagen van de week ná `totEnMet`. */
  dagenNaPeriode: number
}

/**
 * Welk deel van een ISO-week valt binnen `[van, totEnMet]`? `null` wanneer de
 * week de periode helemaal niet raakt.
 */
export function weekBinnenPeriode(
  weekStart: string,
  weekEnd: string,
  van: string,
  totEnMet: string,
): WeekBinnenPeriode | null {
  const periodeStart = weekStart < van ? van : weekStart
  const periodeEind = weekEnd > totEnMet ? totEnMet : weekEnd
  if (periodeStart > periodeEind) return null
  return {
    periodeStart,
    periodeEind,
    dagenVoorPeriode: weekStart < van ? aantalDagenInclusief(weekStart, plusDagen(van, -1)) : 0,
    dagenNaPeriode: weekEnd > totEnMet ? aantalDagenInclusief(plusDagen(totEnMet, 1), weekEnd) : 0,
  }
}

function dagenTekst(n: number): string {
  return `${n} ${n === 1 ? 'dag' : 'dagen'}`
}

function maandNaam(iso: string): string {
  return MAAND_NAMEN[Number(iso.slice(5, 7)) - 1] ?? ''
}

/**
 * Toelichting bij een week die over de grens van de periode loopt, bv.
 * "Week 40 loopt door in oktober (4 dagen daar)." `null` wanneer de week
 * helemaal binnen de periode valt.
 *
 * Ligt de grens op een maandwissel, dan noemen we de andere maand: daar staan
 * de overige dagen van die week. Bij een eigen periode midden in een maand is
 * er geen andere maand om naar te wijzen en blijft de tekst neutraal.
 */
export function weekGrensToelichting(input: {
  week: number
  weekStart: string
  weekEnd: string
  van: string
  totEnMet: string
}): string | null {
  const deel = weekBinnenPeriode(input.weekStart, input.weekEnd, input.van, input.totEnMet)
  if (!deel) return null
  const zinnen: string[] = []

  if (deel.dagenVoorPeriode > 0) {
    const dagVoor = plusDagen(input.van, -1)
    zinnen.push(
      dagVoor.slice(0, 7) !== input.van.slice(0, 7)
        ? `Week ${input.week} begint in ${maandNaam(dagVoor)} (${dagenTekst(deel.dagenVoorPeriode)} daar).`
        : `Week ${input.week} begint voor deze periode (${dagenTekst(deel.dagenVoorPeriode)} daarvoor).`,
    )
  }
  if (deel.dagenNaPeriode > 0) {
    const dagNa = plusDagen(input.totEnMet, 1)
    zinnen.push(
      dagNa.slice(0, 7) !== input.totEnMet.slice(0, 7)
        ? `Week ${input.week} loopt door in ${maandNaam(dagNa)} (${dagenTekst(deel.dagenNaPeriode)} daar).`
        : `Week ${input.week} loopt door na deze periode (${dagenTekst(deel.dagenNaPeriode)} daarna).`,
    )
  }
  return zinnen.length > 0 ? zinnen.join(' ') : null
}

// ─── Verdelen ────────────────────────────────────────────────────────────────

/**
 * Waar een rest belandt die nergens meer in past: de laatste (of eerste) plek
 * met capaciteit, en zonder enige capaciteit het ISO-anker van de week.
 */
function overloopIndex(capaciteit: readonly number[], kant: 'eerste' | 'laatste'): number {
  if (kant === 'laatste') {
    for (let i = capaciteit.length - 1; i >= 0; i--) if ((capaciteit[i] ?? 0) > 0) return i
  } else {
    for (let i = 0; i < capaciteit.length; i++) if ((capaciteit[i] ?? 0) > 0) return i
  }
  return Math.min(ISO_ANKER_INDEX, Math.max(0, capaciteit.length - 1))
}

/** Vul `totaal` vanaf het EINDE: de laatste uren eerst, per plek hooguit de capaciteit. */
function vulAchterwaarts(totaal: number, capaciteit: readonly number[]): number[] {
  const out = capaciteit.map(() => 0)
  let rest = Math.max(0, totaal)
  for (let i = capaciteit.length - 1; i >= 0 && rest > 0; i--) {
    const neem = Math.min(rest, Math.max(0, capaciteit[i] ?? 0))
    out[i] = neem
    rest -= neem
  }
  if (rest > 0 && out.length > 0) out[overloopIndex(capaciteit, 'laatste')] += rest
  return out
}

/** Vul `totaal` vanaf het BEGIN: de eerste uren eerst, per plek hooguit de capaciteit. */
function vulVoorwaarts(totaal: number, capaciteit: readonly number[]): number[] {
  const out = capaciteit.map(() => 0)
  let rest = Math.max(0, totaal)
  for (let i = 0; i < capaciteit.length && rest > 0; i++) {
    const neem = Math.min(rest, Math.max(0, capaciteit[i] ?? 0))
    out[i] = neem
    rest -= neem
  }
  if (rest > 0 && out.length > 0) out[overloopIndex(capaciteit, 'eerste')] += rest
  return out
}

/**
 * Verdeel een geheel `totaal` naar rato over gehele `gewichten`, exact sluitend
 * (grootste-rest-methode; bij gelijke rest wint de vroegste plek).
 *
 * Tellen de gewichten al op tot het totaal, dan komt elk gewicht ongewijzigd
 * terug. Zijn er geen gewichten, dan belandt alles op `terugvalIndex`.
 */
function verdeelGeheel(totaal: number, gewichten: readonly number[], terugvalIndex: number): number[] {
  const out = gewichten.map(() => 0)
  if (out.length === 0 || totaal <= 0) return out
  const som = gewichten.reduce((s, g) => s + Math.max(0, g), 0)
  if (som <= 0) {
    out[Math.min(Math.max(0, terugvalIndex), out.length - 1)] = totaal
    return out
  }
  if (som === totaal) return gewichten.map((g) => Math.max(0, g))

  const exact = gewichten.map((g) => (Math.max(0, g) * totaal) / som)
  let toegekend = 0
  for (let i = 0; i < exact.length; i++) {
    out[i] = Math.floor(exact[i] ?? 0)
    toegekend += out[i] ?? 0
  }
  const volgorde = exact
    .map((e, i) => ({ i, rest: e - Math.floor(e) }))
    .sort((a, b) => b.rest - a.rest || a.i - b.i)
  for (let k = 0, rest = totaal - toegekend; rest > 0; k++, rest--) {
    const plek = volgorde[k % volgorde.length]
    if (plek) out[plek.i] += 1
  }
  return out
}

// ─── De week per dag toerekenen ──────────────────────────────────────────────

/** Eén dag met activiteit in de week (de dag-rows van de weekstaat). */
export interface UrenWeekDagInvoer {
  date: string
  uren: number
  reisKm: number
  reisMin: number
}

export interface UrenWeekVerdelingInput {
  /** Maandag van de ISO-week (`yyyy-MM-dd`). */
  weekStart: string
  /** De dagen met activiteit; dagen zonder activiteit mogen ontbreken. */
  dagen: readonly UrenWeekDagInvoer[]
  /** Week-totalen zoals de weekstaat ze toont (live of bevroren snapshot). */
  totalUren: number
  totalOveruren: number
  totalReisKm: number
  totalReisMin: number
  /** De week-splitsing uit `berekenUrenSplitsing` (per ISO-week, CAO). */
  splitsing: UrenSplitsing
}

/** Het aandeel van één kalenderdag in de week-cijfers. */
export interface UrenDagAandeel {
  date: string
  /**
   * Gewerkte uren op deze dag. Basis + ATV-opbouw + overuren tellen hiertoe op,
   * tenzij de week-splitsing zelf meer is dan de gewerkte uren (het randgeval
   * van `berekenUrenSplitsing` waarin de norm kleiner is dan de ATV-band): het
   * surplus staat dan op de laatste dag met uren, zodat de week-totalen van de
   * splitsing ongewijzigd blijven.
   */
  uren: number
  basisUren: number
  atvOpbouwUren: number
  overuren: number
  /** Het deel van `totalOveruren` (uren boven de norm) dat op deze dag valt. */
  bovenNormUren: number
  reisKm: number
  reisMin: number
}

/**
 * Reken de cijfers van één ISO-week toe aan haar zeven dagen (ma t/m zo).
 *
 * De som over de zeven dagen is voor elk veld exact het week-totaal: dat is de
 * eigenschap waardoor een week zich over twee maanden laat verdelen zonder dat
 * er een uur dubbel telt of wegvalt.
 *
 *  - Uren en reis volgen de dag-rows. Wijkt het week-totaal daarvan af (een
 *    bevroren snapshot naast live rows), dan wordt het totaal naar rato van de
 *    rows verdeeld. Zijn er helemaal geen rows, dan telt de week op haar
 *    donderdag: het ISO-anker dat ook bepaalt bij welk jaar een week hoort.
 *  - De splitsing is chronologisch: overuren zijn de LAATSTE uren van de week,
 *    de ATV-opbouw de uren daar vlak voor, en alles daarvoor is basis.
 */
export function verdeelWeekOverDagen(input: UrenWeekVerdelingInput): UrenDagAandeel[] {
  const datums = Array.from({ length: 7 }, (_, i) => plusDagen(input.weekStart, i))
  const plek = new Map(datums.map((d, i) => [d, i] as const))

  const gewichtUren = datums.map(() => 0)
  const gewichtKm = datums.map(() => 0)
  const gewichtMin = datums.map(() => 0)
  for (const dag of input.dagen) {
    const i = plek.get(dag.date)
    if (i === undefined) continue // dag buiten deze week: hoort hier niet
    gewichtUren[i] = (gewichtUren[i] ?? 0) + naarCenti(dag.uren)
    gewichtKm[i] = (gewichtKm[i] ?? 0) + naarCenti(dag.reisKm)
    gewichtMin[i] = (gewichtMin[i] ?? 0) + Math.round(klem(dag.reisMin))
  }

  const uren = verdeelGeheel(naarCenti(input.totalUren), gewichtUren, ISO_ANKER_INDEX)
  const km = verdeelGeheel(naarCenti(input.totalReisKm), gewichtKm, ISO_ANKER_INDEX)
  const min = verdeelGeheel(Math.round(klem(input.totalReisMin)), gewichtMin, ISO_ANKER_INDEX)

  // Chronologisch: overuren achteraan, ATV-opbouw daarvoor, basis vooraan.
  const over = vulAchterwaarts(naarCenti(input.splitsing.overuren), uren)
  const naOver = uren.map((u, i) => Math.max(0, u - (over[i] ?? 0)))
  const atv = vulAchterwaarts(naarCenti(input.splitsing.atvOpbouwUren), naOver)
  const naAtv = naOver.map((u, i) => Math.max(0, u - (atv[i] ?? 0)))
  const basis = vulVoorwaarts(naarCenti(input.splitsing.basisUren), naAtv)
  // De uren boven de norm zijn per definitie de laatste uren van de week.
  const bovenNorm = vulAchterwaarts(naarCenti(input.totalOveruren), uren)

  return datums.map((date, i) => ({
    date,
    uren: (uren[i] ?? 0) / 100,
    basisUren: (basis[i] ?? 0) / 100,
    atvOpbouwUren: (atv[i] ?? 0) / 100,
    overuren: (over[i] ?? 0) / 100,
    bovenNormUren: (bovenNorm[i] ?? 0) / 100,
    reisKm: (km[i] ?? 0) / 100,
    reisMin: min[i] ?? 0,
  }))
}

/** De som van een reeks dag-aandelen (exact, in honderdsten opgeteld). */
export function somDagAandelen(dagen: readonly UrenDagAandeel[]): Omit<UrenDagAandeel, 'date'> {
  let uren = 0
  let basis = 0
  let atv = 0
  let over = 0
  let bovenNorm = 0
  let km = 0
  let min = 0
  for (const d of dagen) {
    uren += Math.round(d.uren * 100)
    basis += Math.round(d.basisUren * 100)
    atv += Math.round(d.atvOpbouwUren * 100)
    over += Math.round(d.overuren * 100)
    bovenNorm += Math.round(d.bovenNormUren * 100)
    km += Math.round(d.reisKm * 100)
    min += Math.round(d.reisMin)
  }
  return {
    uren: uren / 100,
    basisUren: basis / 100,
    atvOpbouwUren: atv / 100,
    overuren: over / 100,
    bovenNormUren: bovenNorm / 100,
    reisKm: km / 100,
    reisMin: min,
  }
}
